// src/api/features/currencies/mutations.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createCurrency,
  updateCurrency,
  deleteCurrency,
} from "./api";

import { CurrenciesKeys } from "./queries";

import type {
  Currency,
  CreateCurrencyPayload,
  UpdateCurrencyPayload,
} from "./types";

/* ======================================================
 * CREATE – POST /currencies
 * ====================================================== */

export function useCreateCurrencyMutation() {
  const qc = useQueryClient();

  return useMutation<Currency, Error, CreateCurrencyPayload>({
    mutationFn: (payload) => createCurrency(payload),

    onSuccess(created) {
      // Cache the newly created detail
      qc.setQueryData<Currency>(
        CurrenciesKeys.detail(created.id),
        created
      );

      // Refresh all lists
      qc.invalidateQueries({ queryKey: CurrenciesKeys.all });
    },
  });
}

/* ======================================================
 * UPDATE – PUT /currencies/:id
 * ====================================================== */

export function useUpdateCurrencyMutation(id: number | string) {
  const qc = useQueryClient();

  return useMutation<Currency, Error, UpdateCurrencyPayload>({
    mutationFn: (payload) => updateCurrency(id, payload),

    onSuccess(updated) {
      // Update detail cache
      qc.setQueryData<Currency>(
        CurrenciesKeys.detail(id),
        updated
      );

      // Refresh lists
      qc.invalidateQueries({ queryKey: CurrenciesKeys.all });
    },
  });
}

/* ======================================================
 * DELETE – DELETE /currencies/:id
 * ====================================================== */

export function useDeleteCurrencyMutation() {
  const qc = useQueryClient();

  return useMutation<{ message: string } | void, Error, number | string>({
    mutationFn: (id) => deleteCurrency(id),

    onSuccess(_res, id) {
      // Remove detail cache
      qc.removeQueries({ queryKey: CurrenciesKeys.detail(id) });

      // Re-fetch lists
      qc.invalidateQueries({ queryKey: CurrenciesKeys.all });
    },
  });
}
